<template>
  <fieldset class="location-picker wide">
    <legend>{{ label }}</legend>
    <div class="map-tools">
      <label class="address-field">
        <span>Adresse ou lieu</span>
        <input
          v-model.trim="searchText"
          type="search"
          autocomplete="street-address"
          placeholder="Rue, ville ou établissement"
          @keydown.enter.prevent="searchAddress"
        />
      </label>
      <button class="ink-button" type="button" :disabled="searching" @click="searchAddress">
        {{ searching ? 'Recherche…' : 'Chercher' }}
      </button>
      <button class="ink-button locate-map-button" type="button" :disabled="locating" @click="locateMe(false)">
        {{ locating ? 'Localisation…' : 'Ma position' }}
      </button>
    </div>

    <div class="enchanted-map-frame">
      <div ref="mapContainer" class="enchanted-map" :aria-label="`${label} : cliquez sur la carte pour placer le sceau`"></div>
    </div>

    <p class="map-guidance" :data-tone="mapError ? 'error' : 'info'">
      <template v-if="mapError">{{ mapError }}</template>
      <template v-else-if="lat != null && lng != null">
        Sceau placé à {{ lat.toFixed(5) }}, {{ lng.toFixed(5) }}. Cliquez ailleurs sur la carte pour le déplacer.
      </template>
      <template v-else>La carte cherche votre position. Vous pouvez aussi choisir le lieu d’un clic ou rechercher une adresse.</template>
    </p>
  </fieldset>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { L, addOpenFreeMapLayer, OPENFREEMAP_ATTRIBUTION_HTML, type OpenFreeMapLayerHandle } from '../engine/openFreeMap';

const props = withDefaults(defineProps<{
  lat?: number;
  lng?: number;
  label?: string;
  autoLocate?: boolean;
}>(), {
  label: 'Lieu de publication',
  autoLocate: true,
});

const emit = defineEmits<{
  'update:lat': [value: number];
  'update:lng': [value: number];
}>();

const mapContainer = ref<HTMLElement | null>(null);
const searchText = ref('');
const searching = ref(false);
const locating = ref(false);
const mapError = ref('');
let map: any = null;
let marker: any = null;
let currentPositionMarker: any = null;
let baseMapLayer: OpenFreeMapLayerHandle | null = null;
let resizeObserver: ResizeObserver | null = null;

const DEFAULT_CENTER: [number, number] = [48.8566, 2.3522];

function selectedPosition(): [number, number] | null {
  return Number.isFinite(props.lat) && Number.isFinite(props.lng)
    ? [Number(props.lat), Number(props.lng)]
    : null;
}

function createSealIcon() {
  return L.divIcon({
    className: 'mandatary-map-marker',
    html: '<span aria-hidden="true"><i></i></span>',
    iconSize: [38, 48],
    iconAnchor: [19, 46],
  });
}

function placeMarker(lat: number, lng: number, pan = false) {
  if (!map) return;
  if (!marker) {
    marker = L.marker([lat, lng], { icon: createSealIcon(), draggable: true }).addTo(map);
    marker.on('dragend', () => {
      const point = marker.getLatLng();
      selectCoordinates(point.lat, point.lng, false);
    });
  } else {
    marker.setLatLng([lat, lng]);
  }
  if (pan) map.setView([lat, lng], Math.max(map.getZoom(), 17), { animate: true });
}

function selectCoordinates(lat: number, lng: number, pan = false) {
  mapError.value = '';
  emit('update:lat', lat);
  emit('update:lng', lng);
  placeMarker(lat, lng, pan);
}

function showCurrentPosition(lat: number, lng: number) {
  if (!map) return;
  if (currentPositionMarker) currentPositionMarker.setLatLng([lat, lng]);
  else {
    currentPositionMarker = L.circleMarker([lat, lng], {
      radius: 7,
      color: '#f5e3aa',
      weight: 2,
      fillColor: '#65d6ce',
      fillOpacity: .9,
    }).addTo(map).bindTooltip('Votre position actuelle');
  }
}

function locateMe(silent: boolean) {
  if (!navigator.geolocation) {
    if (!silent) mapError.value = 'La géolocalisation n’est pas disponible sur cet appareil.';
    return;
  }
  locating.value = true;
  navigator.geolocation.getCurrentPosition((position) => {
    locating.value = false;
    const lat = position.coords.latitude;
    const lng = position.coords.longitude;
    showCurrentPosition(lat, lng);
    // À la première ouverture, la position actuelle devient la sélection.
    // Ensuite, seul un clic explicite sur « Ma position » remplace le lieu.
    if (!selectedPosition() || !silent) selectCoordinates(lat, lng, true);
  }, () => {
    locating.value = false;
    if (!silent) mapError.value = 'Position indisponible. Choisissez le lieu sur la carte ou recherchez une adresse.';
  }, { enableHighAccuracy: true, timeout: 10_000, maximumAge: 60_000 });
}

async function searchAddress() {
  if (!searchText.value || searching.value) return;
  searching.value = true;
  mapError.value = '';
  try {
    const query = encodeURIComponent(searchText.value);
    const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=1&q=${query}`, {
      headers: { 'Accept-Language': 'fr' },
      referrerPolicy: 'strict-origin-when-cross-origin',
    });
    if (!response.ok) throw new Error('Recherche indisponible');
    const results = await response.json();
    const first = Array.isArray(results) ? results[0] : null;
    if (!first) {
      mapError.value = 'Adresse introuvable. Essayez avec la ville ou le code postal.';
      return;
    }
    selectCoordinates(Number(first.lat), Number(first.lon), true);
  } catch {
    mapError.value = 'La recherche d’adresse est momentanément indisponible. Vous pouvez toujours placer le sceau sur la carte.';
  } finally {
    searching.value = false;
  }
}

async function initMap() {
  if (!mapContainer.value) return;
  try {
    const selected = selectedPosition();
    map = L.map(mapContainer.value, { zoomControl: true, attributionControl: true })
      .setView(selected || DEFAULT_CENTER, selected ? 17 : 13);
    map.attributionControl.setPrefix(false);
    map.attributionControl.addAttribution(OPENFREEMAP_ATTRIBUTION_HTML);
    baseMapLayer = addOpenFreeMapLayer(map, { label: 'guild-mandatary-location-picker' });
    map.on('click', (event: any) => selectCoordinates(event.latlng.lat, event.latlng.lng));
    if (selected) placeMarker(selected[0], selected[1]);
    resizeObserver = new ResizeObserver(() => map?.invalidateSize());
    resizeObserver.observe(mapContainer.value);
    window.setTimeout(() => map?.invalidateSize(), 100);
    if (props.autoLocate) locateMe(true);
  } catch (error: any) {
    mapError.value = error?.message || 'Carte indisponible.';
  }
}

watch(() => [props.lat, props.lng], () => {
  const selected = selectedPosition();
  if (selected) placeMarker(selected[0], selected[1]);
});

onMounted(initMap);
onUnmounted(() => {
  resizeObserver?.disconnect();
  baseMapLayer?.remove();
  baseMapLayer = null;
  map?.remove();
  map = null;
  marker = null;
  currentPositionMarker = null;
});
</script>
